import {attacker_color, defender_color} from "../../../theme";
import React, {ReactNode, useEffect, useRef, useState} from "react";
import {Marker, Polyline, Popup} from "react-leaflet";
import L, {Icon, LatLngExpression, Polyline as LeafletPolyline, Marker as LeafletMarker} from "leaflet";
import {Box, Stack, Typography} from "@mui/material";
import {normalizeVector, positionToMapBounds, scaleVector, SpecifiedPosition} from "../MapRender";
import {CompetitiveRoundSummaries, Maps} from "../../../reckon-api";


export default function PlayerOnMap(props:{prs: {round: CompetitiveRoundSummaries, position?: SpecifiedPosition },
    containerSize:number,
    map: Maps,
    not_unique?: boolean,
    display_details?: boolean
}){
    const {prs, containerSize, map} = props;
    const polylineRef = useRef<LeafletPolyline | null>(null);
    const [position, setPosition] = useState(undefined as [number, number]|undefined);
    const [linePosition, setLinePosition] = useState(undefined as [[number, number], [number, number]]|undefined);

    useEffect(() => {
        if(prs.position && containerSize > 0){
            const posCoord = prs.position.position;
            const viewVec = prs.position.view_vector;
            if (!posCoord || posCoord.x === null || posCoord.y === null) {
                setPosition(undefined);
                setLinePosition(undefined);
                return;
            }
            const position_normalized = positionToMapBounds({x: posCoord.x, y: posCoord.y}, map);
            position_normalized.x = position_normalized.x * containerSize;
            position_normalized.y = position_normalized.y * containerSize;
            
            setPosition([position_normalized.x, position_normalized.y]);
            
            // Use view_vector if available, otherwise try velocity as fallback for direction
            const directionVec = viewVec || prs.position.velocity;
            if (directionVec && (directionVec.x !== null || directionVec.y !== null)) {
                const vx = directionVec.x ?? 0;
                const vy = directionVec.y ?? 0;
                const vz = directionVec.z ?? 0;
                // Check if vector has any magnitude
                if (vx !== 0 || vy !== 0 || vz !== 0) {
                    const normalizedViewVector = normalizeVector({x: vx, y: vy, z: vz});
                    const scaledViewVector = scaleVector(normalizedViewVector, 100);
                    const endPoint = {
                        x: position_normalized.x + scaledViewVector.x,
                        y: position_normalized.y + scaledViewVector.y,
                    };
                    setLinePosition([[position_normalized.x, position_normalized.y], [endPoint.x, endPoint.y]]);
                } else {
                    setLinePosition(undefined);
                }
            } else {
                setLinePosition(undefined);
            }
        } else {
            setPosition(undefined);
            setLinePosition(undefined);
        }
    }, [prs, containerSize, map]);

    useEffect(() => {
        if (polylineRef.current && linePosition) {
            polylineRef.current.setLatLngs(linePosition as LatLngExpression[]);
        }
    }, [linePosition]);

    const icon_url = `https://res.cloudinary.com/xenesis/image/upload/c_fill,h_40,w_40,r_max,bo_2px_solid_rgb:${prs.round.side === 'Attacker' ? attacker_color.replace('#','') : defender_color.replace('#','')}/v1/valAssets/${prs.round.agent}.png`;
    const weapon_url = `https://res.cloudinary.com/xenesis/image/upload/c_limit,w_70,h_35/valAssets/${prs.round.main_weapon}.png`


    if (position) {
        // Normalize and scale view vector

        // Calculate endpoint for the line of view
        return <React.Fragment>
            <AnimatedMarker key={props.not_unique ? `${prs.round.player}-${prs.round.round_id}` : `${prs.round.player}`} iconUrl={icon_url}
                position={position}
            >
                <Popup autoPan={false}>
                    <NeonPlayerPopup round={prs.round} />
                </Popup>
            </AnimatedMarker>
            {props.display_details ? <AnimatedMarker key={props.not_unique ? `${prs.round.player}-${prs.round.round_id}-weapon` : `${prs.round.player}-weapon`} position={position} iconUrl={weapon_url} /> : undefined}
            {linePosition && <AnimatedPolyline linePosition={linePosition} color={prs.round.side === 'Attacker' ? attacker_color : defender_color} key={`${prs.round.player}-line`}/>}
        </React.Fragment>
    } else {
        return <></>
    }
}

// Neon styled popup for player information
function NeonPlayerPopup({ round }: { round: CompetitiveRoundSummaries }) {
    const sideColor = round.side === 'Attacker' ? attacker_color : defender_color;
    
    const agentIconUrl = `https://res.cloudinary.com/xenesis/image/upload/c_fill,h_48,w_48,r_max/v1/valAssets/${round.agent}.png`;
    const weaponIconUrl = `https://res.cloudinary.com/xenesis/image/upload/c_limit,w_80,h_30/valAssets/${round.main_weapon}.png`;
    
    return (
        <Box
            sx={{
                minWidth: '220px',
                background: 'rgba(0, 0, 5, 0.98)',
                borderRadius: '10px',
                overflow: 'hidden',
                position: 'relative',
                border: `1px solid ${sideColor}40`,
                boxShadow: `
                    0 0 20px ${sideColor}30,
                    inset 0 0 30px rgba(0,0,0,0.5)
                `,
            }}
        >
            {/* Neon glow effect */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '-20px',
                    left: '-20px',
                    width: '100px',
                    height: '100px',
                    background: sideColor,
                    opacity: 0.15,
                    filter: 'blur(40px)',
                    borderRadius: '50%',
                    pointerEvents: 'none',
                }}
            />
            
            {/* Header with player name and side indicator */}
            <Box
                sx={{
                    padding: '10px 12px',
                    background: `linear-gradient(135deg, ${sideColor}20 0%, transparent 100%)`,
                    borderBottom: `1px solid ${sideColor}30`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    position: 'relative',
                    zIndex: 1,
                }}
            >
                {/* Agent icon */}
                <Box
                    component="img"
                    src={agentIconUrl}
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: '50%',
                        border: `2px solid ${sideColor}`,
                        boxShadow: `0 0 10px ${sideColor}60`,
                    }}
                />
                
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontSize: '0.85rem',
                            fontWeight: 700,
                            color: '#fff',
                            letterSpacing: '0.5px',
                            textShadow: `0 0 8px ${sideColor}`,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {round.player}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography
                            sx={{
                                fontSize: '0.65rem',
                                color: sideColor,
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '1px',
                            }}
                        >
                            {round.agent}
                        </Typography>
                        <Box
                            sx={{
                                fontSize: '0.55rem',
                                padding: '1px 6px',
                                borderRadius: '3px',
                                background: `${sideColor}25`,
                                color: sideColor,
                                border: `1px solid ${sideColor}40`,
                                fontWeight: 600,
                                textTransform: 'uppercase',
                            }}
                        >
                            {round.side === 'Attacker' ? 'ATK' : 'DEF'}
                        </Box>
                    </Stack>
                </Box>
            </Box>
            
            {/* Content */}
            <Box sx={{ padding: '10px 12px', position: 'relative', zIndex: 1 }}>
                {/* Weapon */}
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Box
                        component="img"
                        src={weaponIconUrl}
                        sx={{
                            maxHeight: '24px',
                            maxWidth: '70px',
                            objectFit: 'contain',
                            filter: 'brightness(0.9) drop-shadow(0 0 4px rgba(255,255,255,0.3))',
                        }}
                    />
                    <Typography
                        sx={{
                            fontSize: '0.65rem',
                            color: 'rgba(255,255,255,0.6)',
                            fontWeight: 500,
                        }}
                    >
                        {round.main_weapon}
                    </Typography>
                </Stack>
                
                {/* KDA Stats */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        mt: 1,
                        padding: '6px 10px',
                        borderRadius: '4px',
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.08)',
                    }}
                >
                    <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>
                        KDA
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#2ecc71' }}>
                        {round.kills}
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)' }}>/</Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#e74c3c' }}>
                        {round.deaths}
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)' }}>/</Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#f39c12' }}>
                        {round.assists}
                    </Typography>
                </Box>
                
                {/* Additional info */}
                <Stack 
                    direction="row" 
                    spacing={1} 
                    sx={{ mt: 1, flexWrap: 'wrap', gap: '4px' }}
                >
                    {round.loadout_value && (
                        <InfoChip label={`$${round.loadout_value.toLocaleString()}`} />
                    )}
                    {round.round_eco_type && (
                        <InfoChip label={round.round_eco_type} />
                    )}
                    {round.kast && (
                        <InfoChip label="KAST" highlight />
                    )}
                </Stack>
            </Box>
            
            {/* Bottom neon accent line */}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    height: '2px',
                    background: sideColor,
                    boxShadow: `0 0 10px ${sideColor}, 0 0 20px ${sideColor}`,
                }}
            />
        </Box>
    );
}

// Helper component for info chips
function InfoChip({ label, highlight = false }: { label: string; highlight?: boolean }) {
    return (
        <Box
            sx={{
                fontSize: '0.55rem',
                padding: '2px 6px',
                borderRadius: '3px',
                background: highlight ? 'rgba(46, 204, 113, 0.15)' : 'rgba(255,255,255,0.06)',
                color: highlight ? '#2ecc71' : 'rgba(255,255,255,0.5)',
                border: highlight ? '1px solid rgba(46, 204, 113, 0.3)' : '1px solid rgba(255,255,255,0.08)',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.3px',
            }}
        >
            {label}
        </Box>
    );
}

interface AnimatedMarkerProps {
    position: LatLngExpression;
    iconUrl: string;
    children?: ReactNode;
    size?: [number, number];
}

const AnimatedMarker: React.FC<AnimatedMarkerProps> = ({ position, iconUrl, children, size }) => {
    const markerRef = useRef<LeafletMarker | null>(null);
    const prevPositionRef = useRef<LatLngExpression | null>(null);

    useEffect(() => {
        if (!markerRef.current) return;

        const marker = markerRef.current;
        const prevPosition = prevPositionRef.current || position;

        let frame = 0;
        const duration = 200; // Duration in ms
        const steps = 10; // Number of animation steps
        const interval = duration / steps;

        function animate() {
            if (!marker) return;
            frame++;

            const [prevLat, prevLng] = prevPosition as [number, number];
            const [newLat, newLng] = position as [number, number];

            const lat = prevLat + ((newLat - prevLat) * frame) / steps;
            const lng = prevLng + ((newLng - prevLng) * frame) / steps;

            marker.setLatLng([lat, lng]);

            if (frame < steps) {
                requestAnimationFrame(animate);
            } else {
                marker.setLatLng(position);
                prevPositionRef.current = position;
            }
        }

        animate();
    }, [position]);

    return (
        <Marker
            ref={(el) => {
                if (el) markerRef.current = el as unknown as LeafletMarker;
            }}
            position={position}
            icon={new Icon({
                // iconSize: size ? size : [40, 40],
                iconAnchor: [20, 20],
                iconUrl: iconUrl,
            })}
            draggable={false}
        >{ children }</Marker>
    );
};

interface AnimatedPolylineProps {
    linePosition: LatLngExpression[];
    color: string;
}

const AnimatedPolyline: React.FC<AnimatedPolylineProps> = ({ linePosition, color }) => {
    const polylineRef = useRef<LeafletPolyline | null>(null);
    const prevPositionsRef = useRef<LatLngExpression[] | null>(null);

    useEffect(() => {
        if (!polylineRef.current) return;

        const polyline = polylineRef.current;
        const prevPositions = prevPositionsRef.current || linePosition;

        let frame = 0;
        const duration = 200; // Duration in ms
        const steps = 10; // Number of animation steps
        const interval = duration / steps;

        function animate() {
            if (!polyline) return;
            frame++;

            const interpolatedPositions = prevPositions.map((prev, index) => {
                const [prevLat, prevLng] = prev as [number, number];
                const [newLat, newLng] = linePosition[index] as [number, number];

                const lat = prevLat + ((newLat - prevLat) * frame) / steps;
                const lng = prevLng + ((newLng - prevLng) * frame) / steps;

                return [lat, lng] as LatLngExpression;
            });

            polyline.setLatLngs(interpolatedPositions);

            if (frame < steps) {
                requestAnimationFrame(animate);
            } else {
                polyline.setLatLngs(linePosition);
                prevPositionsRef.current = linePosition;
            }
        }

        animate();
    }, [linePosition]);

    return (
        <Polyline
            ref={(el) => {
                if (el) polylineRef.current = el as unknown as LeafletPolyline;
            }}
            positions={linePosition}
            pathOptions={{
                color: color,
                weight: 3,
                opacity: 0.8,
            }}
        />
    );
};